import {ArrowNarrowLeftIcon} from '@heroicons/react/outline';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import Spinner from '@/assets/svg/Spinner';
import {Header} from '@/components';
import TransactionReceipt from '@/screens/Transaction/Receipt';
import BusinessTransactionReceipt from '@/screens/Transaction/Receipt/business';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import {transactionService} from '@/services/transactionService';
import type {AfriexAdminTransaction} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {
  formatAdminTransaction,
  formatBusinessAdminTransaction,
} from '@/utils/dashboard';

/**
 * Which transaction the page loads and how it renders it. Chosen by the
 * route (see `MerchantTransactionReceiptPage` below), never inferred from the
 * URL.
 */
export type TransactionReceiptVariant = 'consumer' | 'merchant';

type LoadedReceipt =
  | {variant: 'consumer'; data: AfriexAdminTransaction}
  | {variant: 'merchant'; data: AfriexBusinessTransaction};

type ReceiptState =
  | {status: 'loading'}
  | {status: 'loaded'; receipt: LoadedReceipt}
  | {status: 'failed'};

const loadReceipt = async (
  variant: TransactionReceiptVariant,
  id: string,
): Promise<LoadedReceipt> => {
  if (variant === 'merchant') {
    const transaction = await merchantTransactionService.getBusinessTransaction(
      {id},
    );
    const formatted = await formatBusinessAdminTransaction(transaction);
    return {variant, data: {...transaction, ...formatted}};
  }

  const transaction = await transactionService.getTransaction({id});
  const formatted = await formatAdminTransaction(transaction);
  return {variant, data: {...transaction, ...formatted}};
};

const renderReceipt = (receipt: LoadedReceipt) =>
  receipt.variant === 'merchant' ? (
    <BusinessTransactionReceipt data={receipt.data} />
  ) : (
    <TransactionReceipt data={receipt.data} />
  );

function TransactionReceiptPage({
  variant = 'consumer',
}: {
  variant?: TransactionReceiptVariant;
}) {
  const navigate = useNavigate();
  const params = useParams();
  // Starts as loading, not idle: the receipt body is rendered only from a
  // loaded transaction. Rendering it from an empty object showed a blank
  // consumer receipt and crashes the business one, whose formatters read
  // `sourcePaymentMethod` unguarded.
  const [state, setState] = useState<ReceiptState>({status: 'loading'});
  // Only the latest request may write state, so a slow response for a
  // previous id or variant cannot overwrite the current one.
  const latestRequest = useRef(0);

  const fetchTransactionDetails = useCallback(async () => {
    latestRequest.current += 1;
    const request = latestRequest.current;
    try {
      setState({status: 'loading'});
      const receipt = await loadReceipt(
        variant,
        params.transactionId as string,
      );
      if (request === latestRequest.current) {
        setState({status: 'loaded', receipt});
      }
    } catch (_err) {
      if (request === latestRequest.current) {
        setState({status: 'failed'});
      }
    }
  }, [params.transactionId, variant]);

  useEffect(() => {
    // Fire-and-forget: fetchTransactionDetails catches its own failures, so
    // this only stops an unexpected throw becoming an unhandled rejection.
    fetchTransactionDetails().catch(() => {});
  }, [fetchTransactionDetails]);

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title="Transaction Receipt"
        dataCy="admin-transaction-receipt-heading"
        icon={
          <ArrowNarrowLeftIcon
            className="font-light w-5 h-5 text-black dark:text-white hover:text-indigo-500 cursor-pointer"
            onClick={() => navigate(-1)}
          />
        }
      />

      <div className="flex justify-center items-center flex-col">
        {state.status === 'loading' && (
          <Spinner size={8} className="text-cyan-600" />
        )}
        {state.status === 'failed' && (
          <p
            className="mt-10 text-center text-red-500"
            data-cy="admin-transaction-receipt-error"
          >
            Couldn&apos;t load this transaction.
          </p>
        )}
        {state.status === 'loaded' && renderReceipt(state.receipt)}
      </div>
    </div>
  );
}

/**
 * The merchant receipt route's page: the same page, loading the business
 * transaction and rendering the business receipt (gated on the merchant
 * receipt permission). Route glue only.
 */
export function MerchantTransactionReceiptPage() {
  return <TransactionReceiptPage variant="merchant" />;
}

export default TransactionReceiptPage;
